import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { 
  AcademicCapIcon, 
  CpuChipIcon, 
  CloudIcon, 
  ChartBarIcon, 
  ChatBubbleLeftRightIcon, 
  CogIcon, 
  PuzzlePieceIcon, 
  CalculatorIcon, 
  DocumentTextIcon 
} from '@heroicons/react/24/outline';

const GeniusFramework = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-[#333333]">
      <Helmet>
        <title>Genius Framework | Révolution IA & Neurosciences | Infinity Cloud</title>
        <meta name="description" content="Découvrez Genius Framework : la fusion de l'IA et des neurosciences pour un apprentissage ultra-personnalisé et une efficacité redoutable." />
      </Helmet>

      {/* Hero Section Modifié */}
      <section className="pt-16 pb-8 overflow-hidden relative bg-white">
        <div 
          className="absolute inset-0 opacity-5 z-0" 
          style={{
            backgroundImage: `url('/images/kits/hero-brain-ai-compressed.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(30px)'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-3">
              <span 
                style={{
                  background: 'linear-gradient(135deg, #5e5ce6, #bf5af2)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  paddingBottom: '0.15em',
                  lineHeight: 1.2
                }}
              >
                Genius Framework
              </span>
            </h1>
            <p className="text-2xl md:text-3xl font-light text-[#555] mb-4 max-w-3xl mx-auto">
              La Révolution de l'Apprentissage par la Fusion <span className="font-semibold text-purple-600">IA & Neurosciences</span>
            </p>
            <p className="text-lg text-[#666666] max-w-2xl mx-auto mb-6">
              Libérez votre potentiel avec une plateforme qui comprend comment vous apprenez et s'adapte dynamiquement pour une efficacité maximale.
            </p>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="mt-6 flex justify-center"
            >
              <img 
                src="/images/kits/hero-brain-ai.png"
                alt="Concept central Genius Framework" 
                className="w-full max-w-3xl rounded-2xl shadow-xl"
                style={{ 
                  animation: 'float 6s ease-in-out infinite',
                }}
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Section IA & Neurosciences (Refonte de Core Concept) */}
      <section className="py-10 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <span 
              style={{
                background: 'linear-gradient(135deg, #5e5ce6, #bf5af2)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
              className="text-xl font-medium inline-block mb-3"
            >
              Le Duo Gagnant
            </span>
            <h2 className="text-4xl font-semibold mb-4 text-[#333333]">
              IA Adaptative & Neurosciences Appliquées
            </h2>
            <p className="max-w-3xl mx-auto text-lg text-[#666666]">
              Genius Framework combine la puissance de l'IA pour personnaliser les parcours avec une compréhension profonde de la cognition humaine pour maximiser la rétention.
            </p>
          </motion.div>

          <div className="flex flex-col md:flex-row gap-8 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex-1 border-l-4 border-purple-100 pl-4"
            >
              <h3 className="text-2xl font-semibold mb-3 text-purple-700 flex items-center"><span className="text-3xl mr-2">🧠</span>Fondations Neuroscientifiques</h3>
              <p className="text-[#666666] mb-3">
                Plus qu'une simple plateforme, Genius Framework s'appuie sur la science cognitive pour optimiser la rétention et l'engagement. Nous ne faisons pas qu'enseigner, nous entraînons votre cerveau.
              </p>
              <ul className="list-disc list-inside space-y-2 text-[#555]">
                <li><strong>Répétition Espacée Intelligente :</strong> L'IA détermine les intervalles optimaux pour revoir les concepts, gravant les connaissances dans la mémoire à long terme.</li>
                <li><strong>Apprentissage Actif :</strong> Des exercices interactifs et des rappels de récupération forcent le cerveau à reconstruire activement l'information.</li>
                <li><strong>Gestion de la Charge Cognitive :</strong> Le contenu est séquencé et présenté pour éviter la surcharge et maximiser la compréhension.</li>
                <li><strong>Feedback Constructif & Motivationnel :</strong> Des retours immédiats et ciblés renforcent les bonnes stratégies et maintiennent l'engagement.</li>
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex-1 border-l-4 border-blue-100 pl-4"
            >
              <h3 className="text-2xl font-semibold mb-3 text-blue-700 flex items-center"><span className="text-3xl mr-2">⚙️</span>Moteur IA Adaptatif</h3>
              <p className="text-[#666666] mb-3">
                Le cœur battant du framework. Notre IA ne se contente pas de suivre vos progrès, elle les anticipe et façonne dynamiquement votre parcours pour une croissance exponentielle.
              </p>
              <ul className="list-disc list-inside space-y-2 text-[#555]">
                <li><strong>Analyse Comportementale Fine :</strong> Détection des micro-hésitations, analyse du temps de réponse, identification des patterns d'erreur.</li>
                <li><strong>Génération de Contenu à la Volée :</strong> Création d'exercices, d'explications alternatives et de défis adaptés à *vos* besoins spécifiques.</li>
                <li><strong>Ajustement Dynamique du Parcours :</strong> Le système peut réorganiser les modules, introduire des rappels ou proposer des approfondissements en temps réel.</li>
                <li><strong>Profilage Cognitif Évolutif :</strong> Compréhension de vos préférences d'apprentissage (visuel, auditif, kinesthésique...) pour adapter le format du contenu.</li>
              </ul>
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-8 flex justify-center"
          >
            <img 
              src="/images/kits/cognitive-analysis.png"
              alt="Fusion IA et Neurosciences" 
              className="w-full max-w-2xl rounded-xl shadow-lg"
            />
          </motion.div>
        </div>
      </section>

      {/* Section Timeline (NOUVEAU) */}
      <section className="py-10 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <span 
              style={{
                background: 'linear-gradient(135deg, #5e5ce6, #bf5af2)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
              className="text-xl font-medium inline-block mb-3"
            >
              Notre Parcours
            </span>
            <h2 className="text-4xl font-semibold mb-4 text-[#333333]">
              Timeline de Développement
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-[#666666]">
              De l'idée initiale à la plateforme révolutionnaire d'aujourd'hui.
            </p>
          </motion.div>

          <div className="timeline-container">
            <div className="timeline-item timeline-left">
              <div className="timeline-content">
                <h3>Q1 2023</h3>
                <h4>Conception Initiale</h4>
                <p>Développement du concept "Exam Genius", recherche IA & neurosciences. Framework théorique.</p>
              </div>
            </div>
            <div className="timeline-item timeline-right">
              <div className="timeline-content">
                <h3>Q2-Q3 2023</h3>
                <h4>Développement du Prototype</h4>
                <p>Premiers modèles IA, génération de contenu pédagogique. Tests initiaux avec GPT-4.</p>
              </div>
            </div>
            <div className="timeline-item timeline-left">
              <div className="timeline-content">
                <h3>Q4 2023</h3>
                <h4>Tests Internes</h4>
                <p>Validation concept, ajustement algorithmes & UI. Intégration standards EVA-TEST.</p>
              </div>
            </div>
            <div className="timeline-item timeline-right">
              <div className="timeline-content">
                <h3>Q1 2024</h3>
                <h4>Lancement Bêta</h4>
                <p>Déploiement version bêta restreinte, collecte des retours, amélioration continue.</p>
              </div>
            </div>
            <div className="timeline-item timeline-left">
              <div className="timeline-content">
                <h3>Q2 2024 - Présent</h3>
                <h4>Expansion & Fondation</h4>
                <p>Amélioration continue, base technique pour des apps comme <a href="/apprentiprep" className="text-blue-600 hover:underline">ApprentiPrep</a>.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features (Renommé et contenu affiné) */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <span 
              style={{
                background: 'linear-gradient(135deg, #5e5ce6, #bf5af2)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
              className="text-xl font-medium inline-block mb-3"
            >
              Modules Fondamentaux
            </span>
            <h2 className="text-4xl font-semibold mb-4 text-[#333333]">
              Au Cœur de l'Intelligence Adaptative
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-[#666666]">
               Les piliers technologiques qui permettent la personnalisation dynamique.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0 }}
              className="rounded-[24px] shadow-lg hover:shadow-xl transition-shadow flex flex-col bg-white overflow-hidden"
              whileHover={{ y: -10 }}
            >
              <div className="p-5 flex items-center justify-center h-32" style={{ background: 'linear-gradient(135deg, #5e5ce6, #bf5af2)' }}>
                <CogIcon className="h-16 w-16 text-white opacity-80"/>
              </div>
              <div className="p-5 flex-grow">
                <h3 className="text-xl font-semibold mb-2 mt-1">Module d'Itération Automatique</h3>
                <p className="text-[#666666] flex-grow">Analyse les performances, génère recommandations et contenu ciblé, optimisant dynamiquement le parcours d'apprentissage.</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="rounded-[24px] shadow-lg hover:shadow-xl transition-shadow flex flex-col bg-white overflow-hidden"
              whileHover={{ y: -10 }}
            >
              <div className="p-5 flex items-center justify-center h-32" style={{ background: 'linear-gradient(135deg, #5e5ce6, #bf5af2)' }}>
                <ChartBarIcon className="h-16 w-16 text-white opacity-80"/>
              </div>
              <div className="p-5 flex-grow">
                <h3 className="text-xl font-semibold mb-2 mt-1">Analyse Cognitive Continue</h3>
                <p className="text-[#666666] flex-grow">Surveillance en temps réel des performances et adaptation dynamique du contenu basé sur les résultats individuels et les patterns détectés.</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="rounded-[24px] shadow-lg hover:shadow-xl transition-shadow flex flex-col bg-white overflow-hidden"
              whileHover={{ y: -10 }}
            >
              <div className="p-5 flex items-center justify-center h-32" style={{ background: 'linear-gradient(135deg, #5e5ce6, #bf5af2)' }}>
                <ChatBubbleLeftRightIcon className="h-16 w-16 text-white opacity-80"/>
              </div>
              <div className="p-5 flex-grow">
                <h3 className="text-xl font-semibold mb-2 mt-1">Coach IA Avancé</h3>
                <p className="text-[#666666] flex-grow">Fournit des réponses instantanées, des explications détaillées et un suivi personnalisé pour chaque apprenant.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* NOUVELLE SECTION: Exam Genius */}
      <section className="py-10 bg-[#f9f9f9]">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <span 
              style={{
                background: 'linear-gradient(135deg, #5e5ce6, #bf5af2)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
              className="text-xl font-medium inline-block mb-3"
            >
              Cas d'Usage Principal
            </span>
            <h2 className="text-4xl font-semibold mb-4 text-[#333333]">
              Exam Genius : Le Coaching Réinventé
            </h2>
            <p className="max-w-3xl mx-auto text-lg text-[#666666] mb-6">
              L'application phare construite sur le Genius Framework, démontrant la puissance de l'IA modulaire et du coaching avancé.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center"
          >
            <img 
              src="/images/kits/exam-genius-interface.png"
              alt="Interface Exam Genius" 
              className="rounded-2xl shadow-xl w-full max-w-4xl"
            />
          </motion.div>
          
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center mb-3"><DocumentTextIcon className="h-6 w-6 text-purple-600 mr-2"/><h4 className="font-semibold text-lg text-purple-700">Préparation Standardisée</h4></div>
              <ul className="list-disc list-inside space-y-1 text-sm text-[#555]">
                <li>Tests EVA-compliant (Harmos 9-11e)</li>
                <li>Examens d'études romand (9e-11e)</li>
                <li>Tests d'admission genevois</li>
              </ul>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }} className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center mb-3"><PuzzlePieceIcon className="h-6 w-6 text-blue-600 mr-2"/><h4 className="font-semibold text-lg text-blue-700">Modules Spécialisés</h4></div>
              <ul className="list-disc list-inside space-y-1 text-sm text-[#555]">
                <li>Raisonnement séquentiel</li>
                <li>Histoire Suisse & Europe</li>
                <li>Démocratie & Nationalisme</li>
                <li>Racisme & États autoritaires</li>
              </ul>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }} className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center mb-3"><CalculatorIcon className="h-6 w-6 text-green-600 mr-2"/><h4 className="font-semibold text-lg text-green-700">Mathématiques</h4></div>
              <ul className="list-disc list-inside space-y-1 text-sm text-[#555]">
                <li>Préparation tests génériques</li>
                <li>Exercices personnalisés</li>
                <li>Évaluation continue adaptative</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* User-Centric Design (Conservé, espacement ajusté) */}
      <section className="py-10 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex-1 flex justify-center order-1 md:order-1"
            >
              <img 
                src="/images/kits/dashboard-overview.png"
                alt="Interface utilisateur adaptative Genius Framework" 
                className="rounded-2xl shadow-xl w-full max-w-md"
                style={{ animation: 'float 6s ease-in-out infinite 1s' }}
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex-1 order-2 md:order-2"
            >
              <span 
                style={{
                  background: 'linear-gradient(135deg, #5e5ce6, #bf5af2)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
                className="text-xl font-medium inline-block mb-3"
              >
                Design Centré Utilisateur
              </span>
              <h2 className="text-3xl font-semibold mb-3">Une Expérience Intuitive</h2>
              <p className="text-[#666666] mb-4">
                Le Genius Framework place l'humain au centre, offrant une expérience fluide qui minimise la courbe d'apprentissage et maximise l'engagement.
              </p>
              
              <ul className="space-y-3">
                {[
                  "Visualisations interactives et claires",
                  "Navigation contextuelle intelligente",
                  "Interfaces réactives et adaptatives",
                  "Personnalisation poussée de l'expérience"
                ].map((item, index) => (
                  <motion.li 
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.1 * index }}
                    className="flex items-start"
                  >
                    <svg className="w-5 h-5 mt-1 text-purple-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-[#666666]">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* NOUVELLE SECTION: Architecture Technique */}
      <section className="py-10 bg-gray-100">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <span 
              style={{
                background: 'linear-gradient(135deg, #5e5ce6, #bf5af2)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
              className="text-xl font-medium inline-block mb-3"
            >
              Sous le Capot
            </span>
            <h2 className="text-4xl font-semibold mb-4 text-[#333333]">
              Architecture Technique Avancée
            </h2>
            <p className="max-w-3xl mx-auto text-lg text-[#666666]">
              Notre infrastructure repose sur une architecture Azure AKS Kubernetes robuste et évolutive, conçue pour offrir performance, sécurité et flexibilité.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <motion.div 
              id="infra-card"
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }} 
              transition={{ duration: 0.5, delay: 0.1 }} 
              className="rounded-xl shadow-lg overflow-hidden bg-white"
            >
              <div className="p-5 flex items-center justify-center h-24" style={{ background: 'linear-gradient(135deg, #0066cc, #5ac8fa)' }}>
                 <CloudIcon className="h-12 w-12 text-white opacity-90"/>
              </div>
              <div className="p-5">
                <h3 className="text-2xl font-semibold mb-3 text-blue-800">Infrastructure Cloud Azure</h3>
                <p className="text-[#666666] mb-3">Déployé sur cluster Kubernetes (AKS) avec environnements distincts pour le développement et la production :</p>
                <ul className="list-disc list-inside space-y-2 text-[#555]">
                  <li><strong>Environnement R&D :</strong> Dédié à la recherche et au développement rapide de nouvelles fonctionnalités et modèles IA.</li>
                  <li><strong>Environnement Production :</strong> Le "cerveau" d'Exam Genius, hébergeant l'analytique et l'intelligence artificielle centrale avec haute disponibilité.</li>
                </ul>
                <h4 className="font-semibold text-lg mt-4 mb-2 text-gray-700">Autres Composants Clés :</h4>
                 <ul className="list-disc list-inside space-y-1 text-sm text-[#555]">
                   <li>**Solution Streamlit :** Interface utilisateur interactive pour la visualisation des données et l'interaction fluide avec les modules IA.</li>
                    <li>**Sécurité & Performance :** Isolation des environnements, scaling automatique, optimisation des ressources via Azure.</li>
                 </ul>
              </div>
            </motion.div>

            <motion.div 
              id="tech-components-card"
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }} 
              transition={{ duration: 0.5, delay: 0.2 }} 
              className="rounded-xl shadow-lg overflow-hidden bg-white"
            >
              <div className="p-5 flex items-center justify-center h-24" style={{ background: 'linear-gradient(135deg, #5e5ce6, #bf5af2)' }}>
                 <CpuChipIcon className="h-12 w-12 text-white opacity-90"/>
              </div>
              <div className="p-5">
                <h3 className="text-2xl font-semibold mb-3 text-purple-800">Composants Techniques Clés</h3>
                 <ul className="list-disc list-inside space-y-2 text-[#555]">
                    <li><strong>CloudMind Framework :</strong> Développé par Infinity Cloud, ce framework d'agents IA spécialisés orchestre l'intelligence centrale.</li>
                    <li><strong>Module d'Itération Instantanée :</strong> Système d'amélioration continue basé sur l'analyse des interactions utilisateurs.</li>
                    <li><strong>Client Exam Genius :</strong> Interface utilisateur optimisée avec intégration OpenAI et traitement local des données sensibles.</li>
                    <li><strong>Modules Interconnectés :</strong> Architecture modulaire permettant l'intégration transparente (analytique, génération, contenu, etc.).</li>
                </ul>
                <p className="mt-4 text-sm text-gray-500 italic">Cette architecture modulaire permet une flexibilité et une évolutivité maximales.</p>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-6 flex flex-col items-center"
          >
            <h3 className="text-2xl font-semibold mb-4 text-center text-[#333333]">Architecture Technique d'Ingénierie Moderne</h3>
            <img 
              src="/images/kits/cognitive-analysis.png"
              alt="Diagramme d'architecture technique Genius Framework" 
              className="w-full max-w-4xl rounded-xl shadow-lg"
            />
          </motion.div>
        </div>
      </section>

      {/* Call To Action (Conservé, espacement ajusté) */}
      <section className="py-10 bg-gradient-to-r from-[#5e5ce6] to-[#bf5af2] text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold mb-4">Prêt à Révolutionner l'Apprentissage ?</h2>
            <p className="max-w-2xl mx-auto text-lg mb-6 text-white/90">
              Découvrez comment le Genius Framework peut propulser vos projets EdTech ou de formation interne vers de nouveaux sommets.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-purple-700 font-semibold py-3 px-8 rounded-full text-lg shadow-lg hover:shadow-xl transition-all"
              onClick={() => navigate('/contact')}
            >
              Contactez-nous pour une Démo
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Styles (Animation Float + Timeline CSS) */}
      <style>
        {`
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
            100% { transform: translateY(0px); }
          }
          
          /* Styles pour la Timeline */
          .timeline-container {
            position: relative;
            max-width: 800px;
            margin: 50px auto;
            padding: 20px 0;
          }
          .timeline-container::after {
            content: '';
            position: absolute;
            width: 3px;
            background-color: #5e5ce6;
            top: 0;
            bottom: 0;
            left: 50%;
            margin-left: -1.5px;
          }
          .timeline-item {
            padding: 10px 40px;
            position: relative;
            background-color: inherit;
            width: 50%;
          }
          .timeline-item::after {
            content: '';
            position: absolute;
            width: 15px;
            height: 15px;
            right: -8.5px;
            background-color: white;
            border: 3px solid #5e5ce6;
            top: 25px;
            border-radius: 50%;
            z-index: 1;
          }
          .timeline-left { left: 0; }
          .timeline-right { left: 50%; }
          .timeline-right::after { left: -8.5px; }
          .timeline-content {
            padding: 20px 30px;
            background-color: white;
            position: relative;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
            box-shadow: 0 4px 6px rgba(0,0,0,0.05);
          }
          .timeline-content h3 {
            margin-top: 0;
            font-size: 0.9em;
            font-weight: 600;
            color: #5e5ce6;
            margin-bottom: 4px;
          }
          .timeline-content h4 {
            margin-top: 0px;
            margin-bottom: 8px;
            font-size: 1.2em;
            font-weight: 600;
            color: #333;
          }
          .timeline-content p {
            margin: 0;
            font-size: 0.95em;
            color: #666;
            line-height: 1.5;
          }
          @media screen and (max-width: 768px) {
            .timeline-container::after { left: 15px; }
            .timeline-item { width: 100%; padding-left: 50px; padding-right: 10px; }
            .timeline-item::after { left: 7.5px; }
            .timeline-left, .timeline-right { left: 0%; }
            .timeline-left::after, .timeline-right::after { left: 7.5px; } 
          }
        `}
      </style>
    </div>
  );
};

export default GeniusFramework; 